import { useState } from 'react'
import { SOSSIETY_RED, SOSSIETY_RED_HOVER } from '@/styles/brand'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, DollarSign, Clock, Edit3, ChevronRight } from 'lucide-react'
import { TopNavigation } from '@/components/layout/TopNavigation'
import { Switch } from '@/components/ui/switch'
import { useI18n } from '@/contexts/I18nContext'
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig'
import { useInstructorScheduleIntervals } from '@/features/calendar/hooks/useInstructorScheduleIntervals'
import type { ScheduleInterval } from '@/features/calendar/hooks/useInstructorScheduleIntervals'
import { useStripeConnect } from '@/hooks/useStripeConnect'
import { RollReviewPricingTab } from '@/features/roll-review/components/RollReviewPricingTab'
import { useWalletSummary } from '@/features/wallet/hooks/useWallet'
import { formatCurrency } from '@/lib/pricing'

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  bg:      '#160C0C',
  surface: '#281B1B',
  border:  '#FFFFFF0D',
  text:    '#FFFFFF',
  muted:   '#8D8D8D',
  accent:  SOSSIETY_RED,
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
] as const

const CURRENCY_SYMBOL: Record<string, string> = { BRL: 'R$', USD: '$' }

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (t: string) => t.slice(0, 5)

function formatPrice(value: number, currency: string) {
  if (!value || value === 0) return '—'
  const symbol = CURRENCY_SYMBOL[currency] ?? currency
  return `${symbol} ${value.toFixed(0)} ${currency} / class`
}

function formatDayIntervals(intervals: ScheduleInterval[], toLabel: string) {
  if (intervals.length === 0) return null
  return intervals
    .slice()
    .sort((a, b) => a.start_time.localeCompare(b.start_time))
    .map(iv => `${fmt(iv.start_time)} ${toLabel} ${fmt(iv.end_time)}`)
    .join(' · ')
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <p
    className="px-5 text-xs font-semibold tracking-widest pt-6 pb-2"
    style={{ color: C.muted }}
  >
    {children}
  </p>
)

const NavRow = ({
  icon: Icon,
  label,
  subtitle,
  onClick,
}: {
  icon: React.ElementType
  label: string
  subtitle?: string
  onClick: () => void
}) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-4 px-5 py-4 border-b text-left active:opacity-70 transition-opacity"
    style={{ borderColor: C.border }}
  >
    <Icon className="w-5 h-5 flex-shrink-0" style={{ color: C.muted }} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-white">{label}</p>
      {subtitle && (
        <p className="text-xs mt-0.5" style={{ color: C.muted }}>{subtitle}</p>
      )}
    </div>
    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: C.muted }} />
  </button>
)

// ─── Earnings Widget ──────────────────────────────────────────────────────────

const EarningsWidget = () => {
  const navigate  = useNavigate()
  const { t }     = useI18n()
  const { account, isLoading: stripeLoading } = useStripeConnect()
  const { data: wallet, isLoading: walletLoading } = useWalletSummary()

  const isStripeSetup = !stripeLoading && account?.charges_enabled && account?.payouts_enabled
  const isLoading     = stripeLoading || walletLoading

  if (isLoading) {
    return (
      <div
        className="mx-5 mt-4 rounded-[14px] border animate-pulse flex items-center gap-[14px] px-[17px] py-[15px]"
        style={{ backgroundColor: C.surface, borderColor: C.border }}
      >
        <div className="w-[38px] h-[38px] rounded-[10px] flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />
        <div className="flex-1 flex flex-col gap-1.5">
          <div className="h-2.5 w-16 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <div className="h-5 w-24 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <div className="h-2.5 w-28 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
        </div>
      </div>
    )
  }

  if (!isStripeSetup) {
    return (
      <button
        onClick={() => navigate('/settings/instructor-tools/earnings')}
        className="mx-5 mt-4 w-[calc(100%-2.5rem)] text-left rounded-[14px] border flex items-center gap-[14px] px-[17px] py-[15px] active:opacity-70 transition-opacity"
        style={{ backgroundColor: C.surface, borderColor: `rgba(215,46,22,0.25)` }}
      >
        <div
          className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `rgba(215,46,22,0.08)` }}
        >
          <DollarSign className="w-[18px] h-[18px]" style={{ color: C.accent }} />
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <p className="text-[11px] font-bold tracking-[1.5px] uppercase" style={{ color: C.accent }}>
            {t('instructor_tools.section_earnings')}
          </p>
          <p className="text-sm font-semibold text-white">{t('instructor_tools.earnings_setup_cta')}</p>
          <p className="text-[11px]" style={{ color: C.muted }}>{t('instructor_tools.earnings_setup_cta_subtitle')}</p>
        </div>
        <ChevronRight className="w-[18px] h-[18px] flex-shrink-0" style={{ color: C.muted }} />
      </button>
    )
  }

  const balance    = wallet?.available_balance_cents ?? 0
  const nextPayout = wallet?.next_payout

  return (
    <button
      onClick={() => navigate('/wallet')}
      className="mx-5 mt-4 w-[calc(100%-2.5rem)] text-left rounded-[14px] border flex items-center gap-[14px] px-[17px] py-[15px] active:opacity-70 transition-opacity"
      style={{ backgroundColor: C.surface, borderColor: C.border }}
    >
      <div
        className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `rgba(215,46,22,0.08)` }}
      >
        <DollarSign className="w-[18px] h-[18px]" style={{ color: C.accent }} />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <p className="text-[11px] font-bold tracking-[1.5px] uppercase" style={{ color: C.accent }}>
          {t('instructor_tools.section_earnings')}
        </p>
        <p className="text-[18px] font-bold text-white leading-[27px] tracking-[-0.3px]">
          {formatCurrency(balance, 'USD')}
        </p>
        <p className="text-[11px]" style={{ color: C.muted }}>
          {nextPayout
            ? nextPayout.date
            : t('instructor_tools.earnings_no_payouts')}
        </p>
      </div>
      <ChevronRight className="w-[18px] h-[18px] flex-shrink-0" style={{ color: C.muted }} />
    </button>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const InstructorTools = () => {
  const navigate = useNavigate()
  const { t }    = useI18n()

  const [activeTab, setActiveTab] = useState<'private_classes' | 'roll_review'>('private_classes')

  const { config, isLoading: configLoading, updateConfig } = useAgendaConfig()
  const { intervals, isLoading: intervalsLoading }         = useInstructorScheduleIntervals()
  const handleTogglePublic = async (value: boolean) => {
    await updateConfig({ is_public: value })
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bg }}>
      <TopNavigation />

      <div className="pt-16">
        {/* Header */}
        <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
          <button
            onClick={() => navigate(-1)}
            className="p-1 text-white/80 hover:text-white transition-colors z-10">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">
            {t('instructor_tools.title')}
          </h1>
        </div>

        {/* Earnings Widget */}
        <EarningsWidget />

        {/* Tab bar */}
        <div className="mt-5 flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('private_classes')}
            className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${
              activeTab === 'private_classes'
                ? 'text-white border-b-2 border-sossiety-red'
                : 'text-white/40 border-b-2 border-transparent'
            }`}
          >
            {t('instructor_tools.tab_private_classes')}
          </button>
          <button
            onClick={() => setActiveTab('roll_review')}
            className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${
              activeTab === 'roll_review'
                ? 'text-white border-b-2 border-sossiety-red'
                : 'text-white/40 border-b-2 border-transparent'
            }`}
          >
            {t('instructor_tools.tab_roll_review')}
          </button>
        </div>

        <div className="pb-16">

          {/* ── PRIVATE CLASSES TAB ── */}
          {activeTab === 'private_classes' && (
            <>
              {/* ── PRICING ── */}
              <SectionTitle>{t('instructor_tools.section_pricing')}</SectionTitle>
              <div className="px-5 pb-3 border-b" style={{ borderColor: C.border }}>
                <p className="text-3xl font-bold text-white">
                  {configLoading
                    ? '—'
                    : formatPrice(config?.class_value ?? 0, config?.currency ?? 'USD')}
                </p>
              </div>
              <NavRow
                icon={DollarSign}
                label={t('instructor_tools.edit_pricing')}
                subtitle={t('instructor_tools.edit_pricing_subtitle')}
                onClick={() => navigate('/settings/instructor-tools/pricing')}
              />

              {/* ── DURATION ── */}
              <SectionTitle>{t('instructor_tools.section_duration')}</SectionTitle>
              <div className="px-5 pb-3 border-b" style={{ borderColor: C.border }}>
                <p className="text-3xl font-bold text-white">
                  {configLoading
                    ? '—'
                    : `${config?.default_duration ?? 60} ${t('instructor_tools.min')}`}
                </p>
              </div>
              <NavRow
                icon={Clock}
                label={t('instructor_tools.edit_duration')}
                subtitle={t('instructor_tools.edit_duration_subtitle')}
                onClick={() => navigate('/settings/instructor-tools/duration')}
              />

              {/* ── VISIBILITY ── */}
              <SectionTitle>{t('instructor_tools.section_visibility')}</SectionTitle>
              <div
                className="flex items-center justify-between px-5 py-4 border-b"
                style={{ borderColor: C.border }}
              >
                <div className="flex-1 pr-4">
                  <p className="text-sm font-medium text-white">
                    {t('instructor_tools.make_public')}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: C.muted }}>
                    {t('instructor_tools.make_public_subtitle')}
                  </p>
                </div>
                <Switch
                  checked={config?.is_public ?? false}
                  onCheckedChange={handleTogglePublic}
                  disabled={configLoading}
                />
              </div>

              {/* ── AVAILABLE HOURS ── */}
              <SectionTitle>{t('instructor_tools.section_hours')}</SectionTitle>
              {DAYS.map(day => {
                const dayIntervals = intervals.filter(iv => iv.day_of_week === day.key)
                const label        = intervalsLoading ? '…' : (formatDayIntervals(dayIntervals, t('instructor_tools.to')) ?? '—')
                const empty        = !intervalsLoading && dayIntervals.length === 0

                return (
                  <div
                    key={day.key}
                    className="flex items-start px-5 py-3 border-b"
                    style={{ borderColor: C.border }}
                  >
                    <p className="w-10 text-sm font-medium text-white flex-shrink-0">{day.label}</p>
                    <p
                      className="text-sm leading-snug"
                      style={{ color: empty ? C.muted : C.text }}
                    >
                      {label}
                    </p>
                  </div>
                )
              })}

              <NavRow
                icon={Edit3}
                label={t('instructor_tools.edit_schedule')}
                onClick={() => navigate('/settings/instructor-tools/schedule')}
              />
            </>
          )}

          {/* ── ROLL REVIEW TAB ── */}
          {activeTab === 'roll_review' && <RollReviewPricingTab />}

        </div>
      </div>
    </div>
  )
}

export default InstructorTools
